/* eslint-disable react/prop-types */
import React from "react";
import { Tabs, Tag, InputNumber } from "antd";
import { eeIncentiveTableDomestic } from "@/utils/tariffConstants";

export default function ElectricityTariffTable({
  eTariff,
  mode,
  setETariff,
  eeIncentiveTable = eeIncentiveTableDomestic,
  setEeIncentiveTable = () => {},
}) {
  // mode === true means read-only, mode === false means editing

  const updateRebate = (idx, value) => {
    if (value === null || value < 0) return;
    setEeIncentiveTable((prev) => prev.map((tier, i) => (i === idx ? { ...tier, rebate: value } : tier)));
  };

  // AFA is uniform across tariffs, so the common rate is applied to every tariff
  const commonAfaRate = eTariff?.[0]?.afaRate ?? 0.0259;
  const updateCommonAfaRate = (value) => {
    if (value === null || value < 0) return;
    setETariff((eTariff || []).map((item) => ({ ...item, afaRate: value })));
  };

  const updateTariffField = (id, path, value) => {
    if (value < 0) return;
    const newData = eTariff.map((item) => {
      if (item._id === id) {
        const copy = JSON.parse(JSON.stringify(item));
        const keys = path.split(".");
        let curr = copy;
        for (let i = 0; i < keys.length - 1; i++) {
          if (!curr[keys[i]]) curr[keys[i]] = {};
          curr = curr[keys[i]];
        }
        curr[keys[keys.length - 1]] = value;
        return copy;
      }
      return item;
    });
    setETariff(newData);
  };

  const renderRateInput = (id, path, value, unit, step = 0.01) => {
    if (mode) {
      return (
        <span className="fw-semibold text-dark">
          {value !== undefined && value !== null ? Number(value).toFixed(2) : "0.00"}{" "}
          <small className="text-muted">{unit}</small>
        </span>
      );
    }
    return (
      <div className="d-flex align-items-center justify-content-end gap-1">
        <InputNumber
          size="small"
          min={0}
          step={step}
          value={value}
          onChange={(val) => updateTariffField(id, path, val)}
          style={{ width: 90, textAlign: "right" }}
        />
        <small className="text-muted" style={{ minWidth: 55 }}>
          {unit}
        </small>
      </div>
    );
  };

  const renderTariffCard = (item) => {
    const isDomestic = item?.categoryGroup === "domestic" || item?.name?.toLowerCase()?.includes("domestic");
    const isTou = item?.variant === "tou" || item?.kwh?.varient === "peak";

    return (
      <div key={item._id || item.name} className="card shadow-sm border-0 mb-4 p-3 bg-white rounded-3">
        <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom">
          <div className="d-flex align-items-center gap-2">
            <h5 className="mb-0 fw-bold" style={{ color: "#184C78", fontSize: "16px" }}>
              {item.name}
            </h5>
            <Tag color={isTou ? "blue" : "green"}>{isTou ? "Time-of-Use (ToU)" : "General"}</Tag>
          </div>
          <Tag color="cyan">Table {item.position}</Tag>
        </div>

        <div className="table-responsive">
          <table className="table table-bordered table-hover align-middle mb-0">
            <thead style={{ background: "#F1F5F9" }}>
              <tr>
                <th style={{ width: "45%" }}>Charge Component</th>
                <th style={{ width: "25%" }}>Band / Condition</th>
                <th style={{ width: "30%", textAlign: "right" }}>Rate / Charge</th>
              </tr>
            </thead>
            <tbody>
              {/* Energy Charges */}
              {isDomestic && !isTou && (
                <>
                  <tr>
                    <td>Energy Charge</td>
                    <td>≤ 1,500 kWh</td>
                    <td className="text-end">
                      {renderRateInput(item._id, "energyCharge.rate", item?.energyCharge?.rate ?? 27.03, "sen/kWh")}
                    </td>
                  </tr>
                  <tr>
                    <td>Energy Charge</td>
                    <td>&gt; 1,500 kWh</td>
                    <td className="text-end">
                      {renderRateInput(
                        item._id,
                        "energyCharge.thresholdRate",
                        item?.energyCharge?.thresholdRate ?? 37.03,
                        "sen/kWh"
                      )}
                    </td>
                  </tr>
                </>
              )}

              {isDomestic && isTou && (
                <>
                  <tr>
                    <td>Energy Charge (Peak Period)</td>
                    <td>≤ 1,500 kWh</td>
                    <td className="text-end">
                      {renderRateInput(
                        item._id,
                        "energyCharge.peakRate",
                        item?.energyCharge?.peakRate ?? 28.52,
                        "sen/kWh"
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>Energy Charge (Off-Peak Period)</td>
                    <td>≤ 1,500 kWh</td>
                    <td className="text-end">
                      {renderRateInput(
                        item._id,
                        "energyCharge.offPeakRate",
                        item?.energyCharge?.offPeakRate ?? 24.43,
                        "sen/kWh"
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>Energy Charge (Peak Period)</td>
                    <td>&gt; 1,500 kWh</td>
                    <td className="text-end">
                      {renderRateInput(
                        item._id,
                        "energyCharge.thresholdPeakRate",
                        item?.energyCharge?.thresholdPeakRate ?? 38.52,
                        "sen/kWh"
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>Energy Charge (Off-Peak Period)</td>
                    <td>&gt; 1,500 kWh</td>
                    <td className="text-end">
                      {renderRateInput(
                        item._id,
                        "energyCharge.thresholdOffPeakRate",
                        item?.energyCharge?.thresholdOffPeakRate ?? 34.43,
                        "sen/kWh"
                      )}
                    </td>
                  </tr>
                </>
              )}

              {!isDomestic && !isTou && (
                <tr>
                  <td>Energy Charge</td>
                  <td>For all kWh</td>
                  <td className="text-end">
                    {renderRateInput(item._id, "energyCharge.rate", item?.energyCharge?.rate ?? 27.03, "sen/kWh")}
                  </td>
                </tr>
              )}

              {!isDomestic && isTou && (
                <>
                  <tr>
                    <td>Energy Charge (Peak Period)</td>
                    <td>For all kWh during peak</td>
                    <td className="text-end">
                      {renderRateInput(
                        item._id,
                        "energyCharge.peakRate",
                        item?.energyCharge?.peakRate ?? 28.52,
                        "sen/kWh"
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>Energy Charge (Off-Peak Period)</td>
                    <td>For all kWh during off-peak</td>
                    <td className="text-end">
                      {renderRateInput(
                        item._id,
                        "energyCharge.offPeakRate",
                        item?.energyCharge?.offPeakRate ?? 24.43,
                        "sen/kWh"
                      )}
                    </td>
                  </tr>
                </>
              )}

              {/* Capacity Charge */}
              <tr>
                <td>Capacity Charge</td>
                <td>{item?.capacityCharge?.unit === "RM/kW" ? "Per kW of Maximum Demand" : "For all kWh"}</td>
                <td className="text-end">
                  {renderRateInput(
                    item._id,
                    isTou && item?.capacityCharge?.peakRate !== undefined
                      ? "capacityCharge.peakRate"
                      : "capacityCharge.rate",
                    (isTou && item?.capacityCharge?.peakRate !== undefined
                      ? item?.capacityCharge?.peakRate
                      : item?.capacityCharge?.rate) ?? 0,
                    item?.capacityCharge?.unit || "sen/kWh"
                  )}
                </td>
              </tr>

              {/* Network Charge */}
              <tr>
                <td>Network Charge</td>
                <td>{item?.networkCharge?.unit === "RM/kW" ? "Per kW of Maximum Demand" : "For all kWh"}</td>
                <td className="text-end">
                  {renderRateInput(
                    item._id,
                    isTou && item?.networkCharge?.peakRate !== undefined
                      ? "networkCharge.peakRate"
                      : "networkCharge.rate",
                    (isTou && item?.networkCharge?.peakRate !== undefined
                      ? item?.networkCharge?.peakRate
                      : item?.networkCharge?.rate) ?? 0,
                    item?.networkCharge?.unit || "sen/kWh"
                  )}
                </td>
              </tr>

              {/* Retail Charge */}
              <tr>
                <td>Retail Charge</td>
                <td>{isDomestic ? "Per month (Exempt if ≤ 600 kWh)" : "Per month"}</td>
                <td className="text-end">
                  {renderRateInput(item._id, "retailCharge", item?.retailCharge ?? 0, "RM/month")}
                </td>
              </tr>

              {/* AFA Rate */}
              <tr>
                <td>AFA Rate</td>
                <td>{isDomestic ? "Uniform rate (Exempt if ≤ 600 kWh)" : "Uniform rate"}</td>
                <td className="text-end">
                  {renderRateInput(item._id, "afaRate", item?.afaRate ?? 0.0259, "RM/kWh", 0.0001)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const validTariffs = (eTariff || []).filter(
    (item) => !item.name.toLowerCase().startsWith("tariff ")
  );
  const uniqueTariffs = Array.from(
    new Map(validTariffs.map((item) => [item.name, item])).values()
  );

  const domesticTariffs = uniqueTariffs.filter(
    (t) => t.categoryGroup === "domestic" || t.name === "General Domestic" || t.name === "Domestic ToU"
  );
  const nonDomesticLV = uniqueTariffs.filter(
    (t) => t.categoryGroup === "nonDomesticLV" || t.name?.includes("Low Voltage")
  );
  const nonDomesticMV = uniqueTariffs.filter(
    (t) => t.categoryGroup === "nonDomesticMV" || t.name?.includes("Medium Voltage")
  );
  const nonDomesticHV = uniqueTariffs.filter(
    (t) =>
      (t.categoryGroup === "nonDomesticHV" || t.name?.includes("High Voltage")) &&
      !t.name?.includes("Ultra") &&
      !t.name?.includes("Bulk")
  );
  const nonDomesticUHV = uniqueTariffs.filter(
    (t) =>
      t.categoryGroup === "nonDomesticUHV" ||
      t.categoryGroup === "nonDomesticBulkUHV" ||
      t.name?.includes("Ultra")
  );

  const tabItems = [
    {
      key: "domestic",
      label: "Domestic (Residential)",
      children: <div>{domesticTariffs.map(renderTariffCard)}</div>,
    },
    {
      key: "nonDomesticLV",
      label: "Non-Domestic Low Voltage",
      children: <div>{nonDomesticLV.map(renderTariffCard)}</div>,
    },
    {
      key: "nonDomesticMV",
      label: "Non-Domestic Medium Voltage",
      children: <div>{nonDomesticMV.map(renderTariffCard)}</div>,
    },
    {
      key: "nonDomesticHV",
      label: "Non-Domestic High Voltage",
      children: <div>{nonDomesticHV.map(renderTariffCard)}</div>,
    },
    {
      key: "nonDomesticUHV",
      label: "Ultra-High Voltage",
      children: <div>{nonDomesticUHV.map(renderTariffCard)}</div>,
    },
    {
      key: "incentives",
      label: "Incentives & Operating Rules",
      children: (
        <div className="row g-4">
          <div className="col-lg-7">
            <div className="card shadow-sm border-0 p-3 bg-white rounded-3">
              <h5 className="fw-bold mb-3" style={{ color: "#184C78" }}>
                Energy Efficiency Incentive Rebate Table
              </h5>
              <p className="text-muted small">
                Applies to Domestic &amp; Domestic ToU consumers with monthly consumption ≤ 1,000 kWh, and Low Voltage
                Non-Domestic consumers with monthly consumption ≤ 200 kWh (-25.0 sen/kWh).
              </p>
              <div className="table-responsive">
                <table className="table table-sm table-bordered table-striped mb-0">
                  <thead style={{ background: "#F1F5F9" }}>
                    <tr>
                      <th>Monthly Energy Consumption (kWh)</th>
                      <th>Unit</th>
                      <th className="text-end">Rebate Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eeIncentiveTable.map((item, idx) => (
                      <tr key={`${item.start}-${item.end}`}>
                        <td>
                          {item.start} – {item.end} kWh
                        </td>
                        <td>sen/kWh</td>
                        <td className="text-end text-success fw-bold">
                          {mode ? (
                            `-${Number(item.rebate).toFixed(1)}`
                          ) : (
                            <InputNumber
                              size="small"
                              min={0}
                              step={0.5}
                              value={item.rebate}
                              prefix="-"
                              onChange={(val) => updateRebate(idx, val)}
                              style={{ width: 100 }}
                            />
                          )}
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td>&gt; 1,000 kWh</td>
                      <td>sen/kWh</td>
                      <td className="text-end text-muted">0.0</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="col-lg-5">
            <div className="card shadow-sm border-0 p-3 bg-white rounded-3 mb-3">
              <h5 className="fw-bold mb-3" style={{ color: "#184C78" }}>
                Time-of-Use (ToU) Operating Schedule
              </h5>
              <ul className="list-group list-group-flush small">
                {[
                  { label: "Peak Period (Weekdays):", color: "red", value: "2:00 PM – 10:00 PM (14:00 – 22:00)" },
                  {
                    label: "Off-Peak Period (Weekdays):",
                    color: "green",
                    value: "10:00 PM – 2:00 PM (22:00 – 14:00 next day)",
                  },
                  { label: "Weekends & Public Holidays:", color: "cyan", value: "24 Hours Off-Peak (No Peak Rates)" },
                ].map((row) => (
                  <li
                    key={row.label}
                    className="list-group-item d-flex justify-content-between align-items-center gap-2 px-0"
                  >
                    <strong style={{ whiteSpace: "nowrap" }}>{row.label}</strong>
                    <Tag color={row.color} className="me-0" style={{ whiteSpace: "nowrap" }}>
                      {row.value}
                    </Tag>
                  </li>
                ))}
              </ul>
            </div>

            <div className="card shadow-sm border-0 p-3 bg-white rounded-3">
              <h5 className="fw-bold mb-2" style={{ color: "#184C78" }}>
                Common AFA Rate
              </h5>
              <p className="small text-muted mb-2">
                Uniform Automatic Fuel Adjustment across all electricity tariffs:
              </p>
              <div className="d-flex align-items-center justify-content-between p-2 rounded bg-light">
                <span className="fw-bold">Standard AFA Rate</span>
                {mode ? (
                  <span className="badge bg-primary fs-6">RM {Number(commonAfaRate).toFixed(4)} / kWh</span>
                ) : (
                  <div className="d-flex align-items-center gap-1">
                    <InputNumber
                      size="small"
                      min={0}
                      step={0.0001}
                      precision={4}
                      value={commonAfaRate}
                      onChange={updateCommonAfaRate}
                      style={{ width: 100 }}
                    />
                    <small className="text-muted">RM/kWh</small>
                  </div>
                )}
              </div>
              <small className="text-muted d-block mt-2">
                * Exempted for Domestic consumers when monthly consumption ≤ 600 kWh.
              </small>
            </div>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="electricity-tariff-v2">
      <Tabs defaultActiveKey="domestic" items={tabItems} className="custom-tariff-tabs" />
    </div>
  );
}
